import { Controller } from "@hotwired/stimulus"

// Shows a popover when user selects text in the story body: Highlight (with category), Share.
// Computes character offsets by walking the container's text nodes.
export default class extends Controller {
  static values = {
    storySlug: String,
    highlightsUrl: String,
    createHighlightUrl: String,
    shareSnippetUrl: String,
    highlightNotesUrl: String,
    questionsUrl: String,
    signedIn: Boolean,
    signInPath: String,
    signUpPath: String,
    collectionsUrl: String
  }

  static targets = ["popover", "categorySelect", "highlightBtn", "shareBtn", "removeHighlightBtn", "saveHighlightGroup", "signInPrompt", "ideaModal", "ideaModalQuote", "ideaModalBody", "questionPanel", "questionPanelQuote", "questionPanelBody", "questionForm", "questionList", "questionPanelRemoveHighlight", "ideaTooltip", "addToCollectionWrap", "addToCollectionBtn", "addToCollectionDropdown", "addToCollectionLoading", "addToCollectionList", "addToCollectionEmpty", "addToCollectionNew", "addToCollectionNewTrigger", "addToCollectionNewForm", "addToCollectionNewInput"]

  connect() {
    this.boundOnMouseUp = this.onMouseUp.bind(this)
    this.boundOnTouchEnd = this.onTouchEnd.bind(this)
    this.boundOnTouchStart = this.onTouchStart.bind(this)
    this.boundOnClickOutside = this.onClickOutside.bind(this)
    this.boundQuestionHighlightClick = this.onQuestionHighlightClick.bind(this)
    this.container = this.element.querySelector(".story-body") || this.element
    if (!this.container) return
    this.container.addEventListener("mouseup", this.boundOnMouseUp)
    this.container.addEventListener("touchend", this.boundOnTouchEnd, { passive: true })
    this.container.addEventListener("touchstart", this.boundOnTouchStart, { passive: true })
    this.container.addEventListener("click", this.boundQuestionHighlightClick)
    // Defer so story body text nodes are fully in the DOM (Turbo + paint)
    let attempts = 0
    const run = () => {
      if (this.getContainerTextLength() > 0) {
        this.applySavedHighlights()
      } else if (attempts++ < 20) {
        setTimeout(run, 50)
      }
    }
    requestAnimationFrame(() => setTimeout(run, 0))
  }

  applySavedHighlights() {
    const script = this.element.querySelector("#story-user-highlights") || document.getElementById("story-user-highlights")
    if (!script) return
    let list = []
    try {
      const raw = (script.textContent || "[]").trim()
      list = raw ? JSON.parse(raw) : []
    } catch (_) {
      return
    }
    this.ideaTextByHighlightId = {}
    for (const h of list) {
      if (h.idea_text && String(h.idea_text).trim()) this.ideaTextByHighlightId[String(h.id)] = h.idea_text
    }
    this.attachIdeaTooltips()
    const fullText = this.getContainerText()
    if (!fullText) return
    const totalLength = fullText.length
    // Apply user highlights (insight, idea) from end to start
    const sorted = [...list].sort((a, b) => (b.start_offset ?? 0) - (a.start_offset ?? 0))
    for (const h of sorted) {
      let applied = false
      if (h.quoted_text) applied = this.applyOneHighlightByText(h.quoted_text, h.category, h.id)
      if (!applied && totalLength > 0) {
        const start = Math.max(0, Math.min(h.start_offset ?? 0, totalLength - 1))
        const end = Math.min(totalLength, Math.max(h.end_offset ?? 0, start + 1))
        if (start < end) applied = this.applyOneHighlight(start, end, h.category, null, h.id, null)
      }
    }
    // Apply public question highlights (visible to everyone; same as "ask question" passage)
    const pqScript = this.element.querySelector("#story-public-question-highlights") || document.getElementById("story-public-question-highlights")
    if (pqScript && totalLength > 0) {
      let pqList = []
      try {
        const raw = (pqScript.textContent || "[]").trim()
        pqList = raw ? JSON.parse(raw) : []
      } catch (_) {}
      const pqSorted = [...pqList].sort((a, b) => (b.start_offset ?? 0) - (a.start_offset ?? 0))
      for (const h of pqSorted) {
        const highlightId = h.is_own ? h.id : null
        const anchor = h.paragraph_anchor || (h.start_offset != null && h.end_offset != null ? `s${h.start_offset}_e${h.end_offset}` : null)
        let applied = false
        if (h.quoted_text) applied = this.applyOneHighlightByText(h.quoted_text, "question", highlightId, anchor)
        if (!applied && anchor) {
          const start = Math.max(0, Math.min(h.start_offset ?? 0, totalLength - 1))
          const end = Math.min(totalLength, Math.max(h.end_offset ?? 0, start + 1))
          if (start < end) this.applyOneHighlight(start, end, "question", null, highlightId, anchor)
        }
      }
    }
  }

  attachIdeaTooltips() {
    if (!this.container || !this.hasIdeaTooltipTarget) return
    if (this.ideaTooltipBoundOver) return
    this.ideaTooltipBoundOver = true
    this.ideaTooltipPinned = false
    const tooltip = this.ideaTooltipTarget
    const showAt = (mark) => {
      const id = mark.getAttribute("data-highlight-id")
      const text = this.ideaTextByHighlightId && this.ideaTextByHighlightId[String(id)]
      if (!text) return
      tooltip.textContent = text
      const rect = mark.getBoundingClientRect()
      tooltip.style.left = `${rect.left}px`
      tooltip.style.top = `${rect.top - 8}px`
      tooltip.style.transform = "translateY(-100%)"
      tooltip.classList.remove("hidden")
    }
    const hide = () => {
      if (this.ideaTooltipPinned) return
      tooltip.classList.add("hidden")
    }
    this.container.addEventListener("mouseover", (e) => {
      const mark = e.target.closest && e.target.closest("mark[data-highlight-id]")
      if (!mark) return
      const id = mark.getAttribute("data-highlight-id")
      if (!this.ideaTextByHighlightId || !this.ideaTextByHighlightId[String(id)]) return
      showAt(mark)
    })
    this.container.addEventListener("mouseout", (e) => {
      const mark = e.target.closest && e.target.closest("mark[data-highlight-id]")
      const related = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest("mark[data-highlight-id]")
      if (mark && !related) hide()
      else if (mark && related && mark !== related) hide()
    })
    this.container.addEventListener("click", (e) => {
      const mark = e.target.closest && e.target.closest("mark[data-highlight-id]")
      if (!mark) {
        this.ideaTooltipPinned = false
        hide()
        return
      }
      const id = mark.getAttribute("data-highlight-id")
      if (!this.ideaTextByHighlightId || !this.ideaTextByHighlightId[String(id)]) return
      this.ideaTooltipPinned = !this.ideaTooltipPinned
      if (this.ideaTooltipPinned) showAt(mark)
      else hide()
    })
    this.boundIdeaTooltipClickOutside = (e) => {
      if (this.ideaTooltipPinned && this.hasIdeaTooltipTarget && !this.ideaTooltipTarget.contains(e.target) && this.container && !this.container.contains(e.target)) {
        this.ideaTooltipPinned = false
        hide()
      }
    }
    document.addEventListener("click", this.boundIdeaTooltipClickOutside, true)
  }

  getContainerText() {
    let s = ""
    const walker = document.createTreeWalker(this.container, NodeFilter.SHOW_TEXT, null, false)
    while (walker.nextNode()) s += walker.currentNode.textContent
    return s
  }

  getContainerTextLength() {
    let len = 0
    const walker = document.createTreeWalker(this.container, NodeFilter.SHOW_TEXT, null, false)
    while (walker.nextNode()) len += walker.currentNode.textContent.length
    return len
  }

  applyOneHighlight(startOffset, endOffset, category, _quotedTextFallback, highlightId, paragraphAnchorOverride) {
    if (startOffset == null || endOffset == null || startOffset >= endOffset) return false
    const range = this.getRangeForOffsets(startOffset, endOffset)
    if (!range) return false
    const className = `user-highlight user-highlight--${category || "quote"}`
    const paragraphAnchor = paragraphAnchorOverride || (category === "question" ? `s${startOffset}_e${endOffset}` : null)
    const mark = document.createElement("mark")
    mark.className = className
    mark.setAttribute("data-highlight", "true")
    if (highlightId) mark.setAttribute("data-highlight-id", String(highlightId))
    if (paragraphAnchor) mark.setAttribute("data-paragraph-anchor", paragraphAnchor)
    try {
      range.surroundContents(mark)
      return true
    } catch (_) {
      return this.applyHighlightByBlockSegments(range, className, highlightId, paragraphAnchor)
    }
  }

  applyHighlightByBlockSegments(range, className, highlightId, paragraphAnchor) {
    const segments = this.getRangeSegmentsByBlock(range)
    if (segments.length === 0) return false
    for (let i = segments.length - 1; i >= 0; i--) {
      const seg = segments[i]
      const r = document.createRange()
      r.setStart(seg.startNode, seg.startOffset)
      r.setEnd(seg.endNode, seg.endOffset)
      const mark = document.createElement("mark")
      mark.className = className
      mark.setAttribute("data-highlight", "true")
      if (highlightId) mark.setAttribute("data-highlight-id", String(highlightId))
      if (paragraphAnchor) mark.setAttribute("data-paragraph-anchor", paragraphAnchor)
      try {
        r.surroundContents(mark)
      } catch (_) {}
    }
    return true
  }

  getRangeSegmentsByBlock(range) {
    const BLOCK = /^(P|DIV|H[1-6]|LI|BLOCKQUOTE|PRE|TD|TH)$/i
    const getBlockParent = (node) => {
      let n = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
      while (n && n !== this.container) {
        if (BLOCK.test(n.tagName)) return n
        n = n.parentElement
      }
      return null
    }
    const segments = []
    const root = range.commonAncestorContainer
    const startRoot = root.nodeType === Node.TEXT_NODE ? root.parentElement : root
    const walker = document.createTreeWalker(startRoot, NodeFilter.SHOW_TEXT, null, false)
    let current = { block: null, startNode: null, startOffset: 0, endNode: null, endOffset: 0 }
    let node
    while ((node = walker.nextNode())) {
      if (!range.intersectsNode(node)) continue
      const len = node.textContent.length
      const startInNode = node === range.startContainer ? range.startOffset : 0
      const endInNode = node === range.endContainer ? range.endOffset : len
      if (startInNode >= endInNode) continue
      const block = getBlockParent(node)
      if (block !== current.block && current.endNode !== null) {
        segments.push({ ...current })
        current = { block, startNode: node, startOffset: startInNode, endNode: node, endOffset: endInNode }
      } else {
        if (current.startNode === null) {
          current = { block, startNode: node, startOffset: startInNode, endNode: node, endOffset: endInNode }
        } else {
          current.endNode = node
          current.endOffset = endInNode
        }
      }
    }
    if (current.endNode !== null) segments.push(current)
    return segments
  }

  applyOneHighlightByText(quotedText, category, highlightId, paragraphAnchorOverride) {
    if (!quotedText || typeof quotedText !== "string") return false
    const fullText = this.getContainerText()
    const strip = (s) => s.replace(/\s/g, "")
    const needle = strip(quotedText)
    if (!needle) return false
    const fullStripped = strip(fullText)
    const idx = fullStripped.indexOf(needle)
    if (idx === -1) return false
    const rawPos = this.buildNonWhitespacePositions(fullText)
    if (idx + needle.length > rawPos.length) return false
    const start = rawPos[idx]
    const end = rawPos[idx + needle.length - 1] + 1
    if (start == null || end == null || start >= end) return false
    return this.applyOneHighlight(start, end, category, null, highlightId, paragraphAnchorOverride || null)
  }

  buildNonWhitespacePositions(fullText) {
    const out = []
    for (let i = 0; i < fullText.length; i++) {
      if (!/\s/.test(fullText[i])) out.push(i)
    }
    return out
  }

  getRangeForOffsets(startOffset, endOffset) {
    const total = this.getContainerTextLength()
    const start = Math.max(0, Math.min(startOffset, total))
    const end = Math.min(total, Math.max(endOffset, start + 1))
    if (start >= end) return null
    const walker = document.createTreeWalker(this.container, NodeFilter.SHOW_TEXT, null, false)
    let current = 0
    let startNode = null
    let startOff = 0
    let endNode = null
    let endOff = 0
    while (walker.nextNode()) {
      const node = walker.currentNode
      const len = node.textContent.length
      if (startNode === null && current + len >= start) {
        startNode = node
        startOff = Math.min(start - current, len)
      }
      if (endNode === null && current + len >= end) {
        endNode = node
        endOff = Math.min(end - current, len)
        break
      }
      current += len
    }
    if (!startNode || !endNode) return null
    const range = document.createRange()
    range.setStart(startNode, startOff)
    range.setEnd(endNode, endOff)
    return range
  }

  disconnect() {
    if (this.container) {
      this.container.removeEventListener("mouseup", this.boundOnMouseUp)
      this.container.removeEventListener("touchend", this.boundOnTouchEnd)
      this.container.removeEventListener("touchstart", this.boundOnTouchStart)
      this.container.removeEventListener("click", this.boundQuestionHighlightClick)
    }
    document.removeEventListener("click", this.boundOnClickOutside)
    if (this.boundIdeaTooltipClickOutside) document.removeEventListener("click", this.boundIdeaTooltipClickOutside, true)
  }

  onQuestionHighlightClick(e) {
    const mark = e.target.closest && e.target.closest("mark[data-paragraph-anchor]")
    if (!mark) return
    e.preventDefault()
    e.stopPropagation()
    this.openQuestionPanelFromHighlight(mark)
  }

  openQuestionPanelFromHighlight(mark) {
    const anchor = mark.getAttribute("data-paragraph-anchor")
    const highlightId = mark.getAttribute("data-highlight-id")
    if (!anchor || !this.hasQuestionPanelTarget) return
    this.questionAnchor = anchor
    this.panelHighlightId = highlightId || null
    const match = /^s(\d+)_e(\d+)$/.exec(anchor)
    if (match) {
      this.panelStartOffset = parseInt(match[1], 10)
      this.panelEndOffset = parseInt(match[2], 10)
      this.currentSelection = { start: this.panelStartOffset, end: this.panelEndOffset, quotedText: mark.textContent.trim() }
    }
    if (this.hasQuestionPanelQuoteTarget) this.questionPanelQuoteTarget.textContent = mark.textContent.trim()
    if (this.hasQuestionPanelBodyTarget) this.questionPanelBodyTarget.value = ""
    if (this.hasQuestionPanelRemoveHighlightTarget) {
      this.questionPanelRemoveHighlightTarget.classList.toggle("hidden", !this.panelHighlightId)
    }
    this.questionPanelTarget.classList.remove("hidden")
    this.loadQuestionsForPanel()
    this.questionPanelBodyTarget?.focus()
  }

  onTouchStart() {
    this._lastTouchAt = Date.now()
  }

  onTouchEnd() {
    // On mobile, selection is often committed asynchronously after touchend. Delay before reading it.
    const delay = 250
    setTimeout(() => this.tryShowPopover(), delay)
  }

  onMouseUp() {
    // On touch devices that also fire mouse events, skip to avoid double popover (touchend already scheduled it).
    if (this._lastTouchAt && (Date.now() - this._lastTouchAt < 500)) return
    // Defer so the browser has committed the selection — popover shows as soon as you release, no extra click
    setTimeout(() => this.tryShowPopover(), 0)
  }

  tryShowPopover() {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      this.hidePopover()
      return
    }
    if (!this.container.contains(sel.anchorNode) || !this.container.contains(sel.focusNode)) {
      this.hidePopover()
      return
    }
    let range = sel.getRangeAt(0)
    const quotedText = sel.toString().trim()
    if (!quotedText) {
      this.hidePopover()
      return
    }
    const normRange = this.normalizeRangeToTextNodes(range)
    if (normRange) range = normRange
    const { start, end } = this.getOffsets(range)
    this.currentSelection = { start, end, quotedText, range }
    this.currentHighlightId = this.getHighlightIdIntersectingRange(range)
    this.showPopover(range)
  }

  getHighlightIdIntersectingRange(range) {
    const marks = this.container.querySelectorAll("mark[data-highlight-id]")
    for (const mark of marks) {
      try {
        if (range.intersectsNode(mark)) return mark.getAttribute("data-highlight-id")
      } catch (_) {}
    }
    return null
  }

  getOffsets(range) {
    const norm = this.normalizeRangeToTextNodes(range)
    const r = norm || range
    const start = this.getCharacterOffset(this.container, r.startContainer, r.startOffset)
    const end = this.getCharacterOffset(this.container, r.endContainer, r.endOffset)
    return { start: Math.min(start, end), end: Math.max(start, end) }
  }

  normalizeRangeToTextNodes(range) {
    const start = this.getTextBoundary(range.startContainer, range.startOffset, false)
    const end = this.getTextBoundary(range.endContainer, range.endOffset, true)
    if (!start || !end) return null
    try {
      const r = document.createRange()
      r.setStart(start.node, start.offset)
      r.setEnd(end.node, end.offset)
      return r
    } catch (_) {
      return null
    }
  }

  getTextBoundary(node, offset, isEnd) {
    if (node.nodeType === Node.TEXT_NODE) {
      const o = Math.max(0, Math.min(offset, node.textContent.length))
      return { node, offset: o }
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null
    const children = node.childNodes
    const len = children.length
    if (isEnd) {
      if (offset <= 0) {
        const first = this.getFirstTextNodeInSubtree(node)
        return first ? { node: first, offset: 0 } : null
      }
      const child = children[Math.min(offset - 1, len - 1)]
      const last = this.getLastTextNodeInSubtree(child)
      return last ? { node: last, offset: last.textContent.length } : null
    }
    if (offset >= len) {
      const last = this.getLastTextNodeInSubtree(node)
      return last ? { node: last, offset: last.textContent.length } : null
    }
    const child = children[offset]
    const first = this.getFirstTextNodeInSubtree(child)
    return first ? { node: first, offset: 0 } : null
  }

  getFirstTextNodeInSubtree(el) {
    if (el.nodeType === Node.TEXT_NODE) return el
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false)
    return walker.nextNode() ? walker.currentNode : null
  }

  getLastTextNodeInSubtree(el) {
    if (el.nodeType === Node.TEXT_NODE) return el
    let last = null
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false)
    while (walker.nextNode()) last = walker.currentNode
    return last
  }

  getCharacterOffset(container, node, offset) {
    const r = document.createRange()
    const first = this.getFirstTextNode(container)
    if (!first) return 0
    r.setStart(first, 0)
    try {
      r.setEnd(node, offset)
    } catch (_) {
      return 0
    }
    return r.toString().length
  }

  getFirstTextNode(el) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false)
    return walker.nextNode() ? walker.currentNode : null
  }

  showPopover(range) {
    if (!this.hasPopoverTarget) return
    const rect = range.getBoundingClientRect()
    const pop = this.popoverTarget
    pop.classList.remove("hidden")
    const popH = pop.offsetHeight || 48
    const gap = 8
    let left = rect.left
    let top = rect.top - popH - gap
    const vw = typeof window !== "undefined" ? window.innerWidth : 0
    const vh = typeof window !== "undefined" ? window.innerHeight : 0
    if (vw && vh) {
      left = Math.max(8, Math.min(left, vw - pop.offsetWidth - 8))
      if (top < 8) top = rect.bottom + gap
      top = Math.max(8, Math.min(top, vh - popH - 8))
    }
    pop.style.position = "fixed"
    pop.style.left = `${left}px`
    pop.style.top = `${top}px`
    const onExistingHighlight = !!this.currentHighlightId && this.signedInValue
    if (this.hasSaveHighlightGroupTarget) {
      this.saveHighlightGroupTarget.classList.toggle("hidden", onExistingHighlight)
    }
    if (this.hasHighlightBtnTarget) {
      this.highlightBtnTarget.disabled = !this.signedInValue || onExistingHighlight
      this.highlightBtnTarget.title = onExistingHighlight ? "Already highlighted" : (this.signedInValue ? "" : "Sign in to highlight")
      const cat = this.hasCategorySelectTarget ? this.categorySelectTarget.value : "quote"
      this.highlightBtnTarget.textContent = cat === "question" ? "Ask question" : "Save"
    }
    if (this.hasRemoveHighlightBtnTarget) {
      this.removeHighlightBtnTarget.classList.toggle("hidden", !onExistingHighlight)
    }
    if (this.hasAddToCollectionWrapTarget) {
      this.addToCollectionWrapTarget.classList.toggle("hidden", !onExistingHighlight)
    }
    document.addEventListener("click", this.boundOnClickOutside, true)
  }

  updateHighlightButtonLabel() {
    if (!this.hasHighlightBtnTarget || !this.hasCategorySelectTarget) return
    const cat = this.categorySelectTarget.value
    this.highlightBtnTarget.textContent = cat === "question" ? "Ask question" : "Save"
  }

  hidePopover() {
    if (this.hasPopoverTarget) this.popoverTarget.classList.add("hidden")
    this.currentSelection = null
    this.currentHighlightId = null
    this.closeAddToCollection()
    document.removeEventListener("click", this.boundOnClickOutside, true)
  }

  onClickOutside(e) {
    if (!this.hasPopoverTarget || this.popoverTarget.contains(e.target) || this.container.contains(e.target)) return
    if (this.hasIdeaModalTarget && !this.ideaModalTarget.classList.contains("hidden") && this.ideaModalTarget.contains(e.target)) return
    if (this.hasQuestionPanelTarget && !this.questionPanelTarget.classList.contains("hidden") && this.questionPanelTarget.contains(e.target)) return
    if (this.hasAddToCollectionDropdownTarget && this.addToCollectionDropdownTarget.contains(e.target)) return
    this.closeAddToCollection()
    this.hidePopover()
  }

  highlight(e) {
    e.preventDefault()
    if (!this.currentSelection || !this.signedInValue || this.currentHighlightId) return
    const category = this.hasCategorySelectTarget ? this.categorySelectTarget.value : "quote"
    if (category === "idea") {
      this.openIdeaModal()
      return
    }
    if (category === "question") {
      this.openQuestionPanel()
      return
    }
    this.saveHighlight(category, null)
  }

  openIdeaModal() {
    if (!this.hasIdeaModalTarget || !this.currentSelection) return
    this.pendingIdeaSelection = this.currentSelection
    if (this.hasIdeaModalQuoteTarget) this.ideaModalQuoteTarget.textContent = this.currentSelection.quotedText
    if (this.hasIdeaModalBodyTarget) this.ideaModalBodyTarget.value = ""
    this.ideaModalTarget.classList.remove("hidden")
    this.ideaModalBodyTarget?.focus()
  }

  closeIdeaModal() {
    this.ideaModalTarget?.classList.add("hidden")
  }

  submitIdea(e) {
    e.preventDefault()
    if (this.pendingIdeaSelection) this.currentSelection = this.pendingIdeaSelection
    if (!this.currentSelection || !this.signedInValue || !this.hasIdeaModalBodyTarget) return
    const ideaText = this.ideaModalBodyTarget.value.trim()
    if (!ideaText) return
    this.saveHighlight("idea", (highlightId) => this.saveIdeaNote(highlightId, ideaText))
  }

  saveIdeaNote(highlightId, body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const formData = new FormData()
    formData.append("highlight_id", highlightId)
    formData.append("highlight_note[body]", body)
    formData.append("highlight_note[visibility]", "private")
    fetch(this.highlightNotesUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, Accept: "application/json" },
      body: formData
    }).then(() => {
      if (!this.ideaTextByHighlightId) this.ideaTextByHighlightId = {}
      this.ideaTextByHighlightId[String(highlightId)] = body
      this.pendingIdeaSelection = null
      this.closeIdeaModal()
      this.hidePopover()
      window.getSelection()?.removeAllRanges()
    })
  }

  openQuestionPanel() {
    if (!this.hasQuestionPanelTarget || !this.currentSelection) return
    this.panelHighlightId = null
    if (this.hasQuestionPanelRemoveHighlightTarget) this.questionPanelRemoveHighlightTarget.classList.add("hidden")
    this.questionPanelTarget.classList.remove("hidden")
    if (this.hasQuestionPanelQuoteTarget) this.questionPanelQuoteTarget.textContent = this.currentSelection.quotedText
    if (this.hasQuestionPanelBodyTarget) this.questionPanelBodyTarget.value = ""
    this.questionAnchor = `s${this.currentSelection.start}_e${this.currentSelection.end}`
    this.panelStartOffset = this.currentSelection.start
    this.panelEndOffset = this.currentSelection.end
    this.loadQuestionsForPanel()
    this.questionPanelBodyTarget?.focus?.()
  }

  closeQuestionPanel() {
    this.questionPanelTarget?.classList.add("hidden")
    this.panelHighlightId = null
  }

  removeHighlightFromPanel(e) {
    e.preventDefault()
    const id = this.panelHighlightId
    if (!id || !this.signedInValue) return
    const url = `${this.createHighlightUrlValue.replace(/\/$/, "")}/${id}`
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": token, Accept: "application/json" }
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText)
        this.unwrapHighlightMarks(id)
        this.panelHighlightId = null
        this.closeQuestionPanel()
      })
      .catch(() => this.closeQuestionPanel())
  }

  loadQuestionsForPanel() {
    if (!this.hasQuestionListTarget || !this.questionsUrlValue) return
    const url = `${this.questionsUrlValue}?paragraph_anchor=${encodeURIComponent(this.questionAnchor || "")}`
    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((questions) => {
        const emptyMsg = this.signedInValue
          ? '<p class="text-sm text-ink-muted">No questions yet. Ask the first one.</p>'
          : '<p class="text-sm text-ink-muted">No questions yet. Sign in above to ask the first one.</p>'
        this.questionListTarget.innerHTML = questions.length === 0
          ? emptyMsg
          : questions.map((q) => this.renderQuestion(q)).join("")
      })
      .catch(() => {
        this.questionListTarget.innerHTML = '<p class="text-sm text-ink-muted">Could not load questions.</p>'
      })
  }

  avatarHtml(user) {
    const name = (user?.name || user?.username || "User").toString().trim()
    const initials = name ? name.split(/\s+/).map((s) => s[0]).join("").toUpperCase().slice(0, 2) : "?"
    const url = user?.avatar_url
    if (url) return `<img src="${this.escapeHtml(url)}" alt="" class="question-panel-avatar h-6 w-6 rounded-full object-cover shrink-0" role="presentation" />`
    return `<span class="question-panel-avatar question-panel-avatar-fallback flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink/10 text-[10px] font-semibold text-ink" aria-hidden="true">${this.escapeHtml(initials)}</span>`
  }

  renderQuestion(q) {
    const replies = (q.replies || []).map((r) => `<div class="ml-3 mt-2 text-sm"><p class="text-ink">${this.escapeHtml(r.body)}</p><div class="mt-1 flex items-center">${this.avatarHtml(r.user)}</div></div>`).join("")
    const redirectTo = encodeURIComponent(window.location.href)
    const signInUrl = this.signInPathValue ? `${this.signInPathValue}${this.signInPathValue.includes("?") ? "&" : "?"}redirect_to=${redirectTo}` : "/users/sign_in"
    const signUpUrl = this.signUpPathValue ? `${this.signUpPathValue}${this.signUpPathValue.includes("?") ? "&" : "?"}redirect_to=${redirectTo}` : "/users/sign_up"
    const replyBlock = this.signedInValue
      ? `<form class="mt-2 reply-form" data-parent-id="${this.escapeHtml(String(q.id))}" data-action="submit->text-selection#submitReply"><textarea class="w-full rounded bg-transparent px-2 py-1 text-sm placeholder:text-ink-muted focus:outline-none" rows="2" placeholder="Write a reply..." name="body"></textarea><button type="submit" class="mt-1 text-sm text-accent hover:underline">Post reply</button></form>`
      : `<p class="mt-2 text-xs text-ink-muted"><a href="${this.escapeHtml(signInUrl)}" data-turbo-frame="_top" class="text-accent hover:underline">Sign in</a> or <a href="${this.escapeHtml(signUpUrl)}" data-turbo-frame="_top" class="text-accent hover:underline">Sign up</a> to reply</p>`
    return `<div class="rounded-lg bg-ink/5 p-3" data-question-id="${q.id}"><p class="text-ink text-sm">${this.escapeHtml(q.body)}</p><div class="mt-1 flex items-center">${this.avatarHtml(q.user)}</div>${replies}<div class="mt-2">${replyBlock}</div></div>`
  }

  escapeHtml(s) {
    const div = document.createElement("div")
    div.textContent = s
    return div.innerHTML
  }

  escapeAttr(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }

  submitQuestion(e) {
    e.preventDefault()
    if (!this.signedInValue || !this.hasQuestionPanelBodyTarget) return
    const body = this.questionPanelBodyTarget.value.trim()
    if (!body) return
    const hasSelection = this.currentSelection && this.currentSelection.start != null && this.currentSelection.end != null
    const hasPanelAnchor = this.questionAnchor != null && this.panelStartOffset != null && this.panelEndOffset != null
    if (hasSelection) {
      this.saveHighlight("question", (highlightId) => this.saveParagraphQuestion(body, highlightId))
    } else if (hasPanelAnchor) {
      this.saveParagraphQuestion(body, null)
    }
  }

  submitReply(e) {
    e.preventDefault()
    const form = e.target
    if (!form || !this.signedInValue) return
    const parentId = form.dataset?.parentId
    const bodyEl = form.querySelector('textarea[name="body"]')
    const body = bodyEl?.value?.trim()
    if (!body || !parentId) return
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const payload = {
      paragraph_question: {
        paragraph_anchor: this.questionAnchor || "",
        start_offset: this.panelStartOffset ?? 0,
        end_offset: this.panelEndOffset ?? 0,
        body,
        parent_question_id: parseInt(parentId, 10)
      }
    }
    fetch(this.questionsUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token, Accept: "application/json" },
      body: JSON.stringify(payload)
    })
      .then((r) => {
        if (r.status === 401) {
          window.location.href = `/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}`
          return
        }
        if (!r.ok) throw new Error(r.statusText)
        return r.json()
      })
      .then((data) => { if (data) { bodyEl.value = ""; this.loadQuestionsForPanel() } })
      .catch(() => {})
  }

  saveParagraphQuestion(body, _highlightId) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const start = this.currentSelection?.start ?? this.panelStartOffset ?? 0
    const end = this.currentSelection?.end ?? this.panelEndOffset ?? 0
    const payload = {
      paragraph_question: {
        paragraph_anchor: this.questionAnchor || "",
        start_offset: start,
        end_offset: end,
        body
      }
    }
    fetch(this.questionsUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        Accept: "application/json"
      },
      body: JSON.stringify(payload)
    })
      .then((r) => {
        if (r.status === 401) {
          window.location.href = `/users/sign_in?redirect_to=${encodeURIComponent(window.location.href)}`
          return
        }
        if (!r.ok) throw new Error(r.statusText)
        return r.json()
      })
      .then((data) => {
        if (data) {
          this.questionPanelBodyTarget.value = ""
          this.loadQuestionsForPanel()
          this.hidePopover()
          this.closeQuestionPanel()
          window.getSelection()?.removeAllRanges()
        }
      })
      .catch(() => {})
  }

  saveHighlight(category, afterSave) {
    const url = this.createHighlightUrlValue
    const body = JSON.stringify({
      highlight: {
        start_offset: this.currentSelection.start,
        end_offset: this.currentSelection.end,
        quoted_text: this.currentSelection.quotedText,
        category
      }
    })
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        Accept: "application/json"
      },
      body
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText)
        return res.json()
      })
      .then((data) => {
        const cat = this.hasCategorySelectTarget ? this.categorySelectTarget.value : "quote"
        this.applyOneHighlight(this.currentSelection.start, this.currentSelection.end, cat, null, data?.id, null)
        if (afterSave && typeof afterSave === "function") {
          afterSave(data?.id)
        } else {
          this.currentHighlightId = data?.id ?? this.currentHighlightId
          if (this.currentHighlightId) {
            if (this.hasSaveHighlightGroupTarget) this.saveHighlightGroupTarget.classList.add("hidden")
            if (this.hasHighlightBtnTarget) this.highlightBtnTarget.disabled = true
            if (this.hasRemoveHighlightBtnTarget) this.removeHighlightBtnTarget.classList.remove("hidden")
            if (this.hasAddToCollectionWrapTarget) this.addToCollectionWrapTarget.classList.remove("hidden")
          } else {
            this.hidePopover()
            window.getSelection()?.removeAllRanges()
          }
        }
        this.dispatch("highlighted", { detail: { category: cat } })
      })
      .catch(() => this.hidePopover())
  }

  toggleAddToCollection(e) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.signedInValue || !this.currentHighlightId || !this.hasAddToCollectionDropdownTarget) return
    if (this.addToCollectionDropdownTarget.classList.contains("hidden")) {
      this.openAddToCollection()
    } else {
      this.closeAddToCollection()
    }
  }

  openAddToCollection() {
    if (!this.hasAddToCollectionDropdownTarget || !this.collectionsUrlValue) return
    this.addToCollectionDropdownTarget.classList.remove("hidden")
    if (!this._collectionsCache) {
      this.loadCollections()
    } else {
      this.addToCollectionLoadingTarget?.classList.add("hidden")
      this.addToCollectionListTarget?.classList.remove("hidden")
      this.addToCollectionEmptyTarget?.classList.add("hidden")
      if (this._collectionsCache.length === 0 && this.hasAddToCollectionEmptyTarget) {
        this.addToCollectionListTarget?.classList.add("hidden")
        this.addToCollectionEmptyTarget.classList.remove("hidden")
      }
    }
    this._addToCollectionClose = (e) => {
      if (this.addToCollectionDropdownTarget.contains(e.target) || (this.hasAddToCollectionBtnTarget && this.addToCollectionBtnTarget.contains(e.target))) return
      this.closeAddToCollection()
    }
    setTimeout(() => document.addEventListener("click", this._addToCollectionClose, true), 0)
  }

  closeAddToCollection() {
    if (this._addToCollectionClose) {
      document.removeEventListener("click", this._addToCollectionClose, true)
      this._addToCollectionClose = null
    }
    if (this.hasAddToCollectionDropdownTarget) this.addToCollectionDropdownTarget.classList.add("hidden")
    if (this.hasAddToCollectionLoadingTarget) this.addToCollectionLoadingTarget.classList.remove("hidden")
    if (this.hasAddToCollectionListTarget) this.addToCollectionListTarget.classList.add("hidden")
    if (this.hasAddToCollectionEmptyTarget) this.addToCollectionEmptyTarget.classList.add("hidden")
    this.hideNewCollectionForm()
  }

  showNewCollectionForm() {
    if (this.hasAddToCollectionNewFormTarget && this.hasAddToCollectionNewTriggerTarget) {
      this.addToCollectionNewFormTarget.classList.remove("hidden")
      this.addToCollectionNewTriggerTarget.classList.add("hidden")
      if (this.hasAddToCollectionNewInputTarget) {
        this.addToCollectionNewInputTarget.value = ""
        this.addToCollectionNewInputTarget.focus()
      }
    }
  }

  hideNewCollectionForm() {
    if (this.hasAddToCollectionNewFormTarget && this.hasAddToCollectionNewTriggerTarget) {
      this.addToCollectionNewFormTarget.classList.add("hidden")
      this.addToCollectionNewTriggerTarget.classList.remove("hidden")
      if (this.hasAddToCollectionNewInputTarget) this.addToCollectionNewInputTarget.value = ""
    }
  }

  createCollectionAndAdd(e) {
    e.preventDefault()
    if (!this.hasAddToCollectionNewInputTarget || !this.currentHighlightId || !this.collectionsUrlValue) return
    const name = this.addToCollectionNewInputTarget.value.trim()
    if (!name) return
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const submitBtn = this.addToCollectionNewFormTarget?.querySelector('button[type="submit"]')
    if (submitBtn) submitBtn.disabled = true
    fetch(this.collectionsUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", Accept: "application/json" },
      body: JSON.stringify({ knowledge_collection: { name } })
    })
      .then((res) => {
        if (!res.ok) throw new Error("Create failed")
        return res.json()
      })
      .then((collection) => {
        const id = collection?.id
        if (!id) throw new Error("No id")
        this._collectionsCache = this._collectionsCache || []
        this._collectionsCache.unshift({ id, name: collection.name || name })
        const itemsUrl = `${this.collectionsUrlValue.replace(/\/$/, "")}/${id}/items`
        return fetch(itemsUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", Accept: "application/json" },
          body: JSON.stringify({ collection_item: { highlight_id: this.currentHighlightId } })
        }).then(() => collection)
      })
      .then(() => {
        this.hideNewCollectionForm()
        if (this.hasAddToCollectionListTarget && this.hasAddToCollectionEmptyTarget) {
          this.addToCollectionEmptyTarget.classList.add("hidden")
          this.addToCollectionListTarget.classList.remove("hidden")
          this.addToCollectionListTarget.innerHTML = this._collectionsCache
            .map((c) => `<button type="button" data-collection-id="${this.escapeAttr(String(c.id))}" data-action="click->text-selection#addToCollection" class="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-ink/5 transition-colors">${this.escapeHtml(c.name || "Collection")}</button>`)
            .join("")
        }
        this.closeAddToCollection()
      })
      .catch(() => {})
      .finally(() => {
        if (submitBtn) submitBtn.disabled = false
      })
  }

  loadCollections() {
    if (!this.hasAddToCollectionLoadingTarget || !this.hasAddToCollectionListTarget || !this.hasAddToCollectionEmptyTarget) return
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    fetch(this.collectionsUrlValue, { headers: { Accept: "application/json", "X-CSRF-Token": token || "" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        this._collectionsCache = Array.isArray(list) ? list : []
        this.addToCollectionLoadingTarget.classList.add("hidden")
        if (this._collectionsCache.length === 0) {
          this.addToCollectionListTarget.classList.add("hidden")
          this.addToCollectionEmptyTarget.classList.remove("hidden")
        } else {
          this.addToCollectionEmptyTarget.classList.add("hidden")
          this.addToCollectionListTarget.classList.remove("hidden")
          this.addToCollectionListTarget.innerHTML = this._collectionsCache
            .map((c) => `<button type="button" data-collection-id="${this.escapeAttr(String(c.id))}" data-action="click->text-selection#addToCollection" class="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-ink/5 transition-colors">${this.escapeHtml(c.name || "Collection")}</button>`)
            .join("")
        }
      })
      .catch(() => {
        this._collectionsCache = []
        this.addToCollectionLoadingTarget.classList.add("hidden")
        this.addToCollectionListTarget.classList.add("hidden")
        this.addToCollectionEmptyTarget.classList.remove("hidden")
      })
  }

  addToCollection(e) {
    const btn = e.target.closest("[data-collection-id]")
    if (!btn || !this.currentHighlightId || !this.collectionsUrlValue) return
    e.preventDefault()
    const collectionId = btn.getAttribute("data-collection-id")
    const url = `${this.collectionsUrlValue.replace(/\/$/, "")}/${collectionId}/items`
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const origText = btn.textContent
    btn.disabled = true
    btn.textContent = "Adding…"
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", Accept: "application/json" },
      body: JSON.stringify({ collection_item: { highlight_id: this.currentHighlightId } })
    })
      .then((res) => {
        if (res.ok) {
          btn.textContent = "Added"
          this.closeAddToCollection()
        } else {
          btn.disabled = false
          btn.textContent = origText
        }
      })
      .catch(() => {
        btn.disabled = false
        btn.textContent = origText
      })
  }

  removeHighlight(e) {
    e.preventDefault()
    const id = this.currentHighlightId
    if (!id || !this.signedInValue) return
    const url = `${this.createHighlightUrlValue.replace(/\/$/, "")}/${id}`
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": token, Accept: "application/json" }
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText)
        this.unwrapHighlightMarks(id)
        this.hidePopover()
        window.getSelection()?.removeAllRanges()
      })
      .catch(() => this.hidePopover())
  }

  unwrapHighlightMarks(highlightId) {
    const selector = `mark[data-highlight-id="${highlightId}"]`
    this.container.querySelectorAll(selector).forEach((mark) => {
      const parent = mark.parentNode
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
      parent.removeChild(mark)
    })
  }

  share(e) {
    e.preventDefault()
    if (!this.currentSelection) return
    const u = new URL(this.shareSnippetUrlValue)
    u.searchParams.set("quote", this.currentSelection.quotedText)
    const url = u.toString()
    navigator.clipboard.writeText(url).then(() => {
      this.hidePopover()
      window.getSelection()?.removeAllRanges()
      this.dispatch("shared", { detail: { url } })
    }).catch(() => {
      window.open(url, "_blank")
      this.hidePopover()
    })
  }
}
